import axios from 'axios';
import { SoilSessionFact } from './soilSessionService';

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const base = '/api/v1/nutrition';

export interface SoilProgramEntry { orchardName: string; month: string; productName: string; kg: number }
export interface SoilSuggestion { name: string; suggestion: string; score: number }
export interface SoilProgramValidation {
  unknownProducts: string[];
  unknownOrchards: string[];
  duplicateOrchardRows: string[];
  duplicateColumns: { month: string; productName: string }[];
  /** U4, additive: only names that HAVE a server-side suggestion appear here. */
  suggestions?: { products: SoilSuggestion[]; orchards: SoilSuggestion[] };
}
export interface SoilProgram {
  _id: string;
  season: string;
  status: 'received' | 'reviewed' | 'active' | 'superseded';
  entries: SoilProgramEntry[];
  source?: { vendor?: string; contact?: string; filename?: string };
  createdAt: string;
  validation?: SoilProgramValidation;
}
export interface ImportResult { program: SoilProgram; validation: SoilProgramValidation; warnings: string[] }

export interface SoilBoardCellSession {
  _id: string;
  status: string;
  groupTotal: number;
  groupComplete: number;
  groupSignedOff: number;
  groupAwaitingSignoff: number;
  unassignedOrchards: number;
  appliedKg: number;
  oldestAwaitingCompletedAt: string | null;
}
export interface SoilBoardCell {
  month: string;
  productName: string;
  orchardCount: number;
  plannedKg: number;
  session: SoilBoardCellSession | null;
}
export interface SoilBoardProduct {
  productName: string;
  analysis: { n: number; p: number; k: number } | null;
  seasonKg: number;
}
export interface SoilBoard {
  program: { _id: string; season: string; status: string; source?: { vendor?: string; contact?: string } };
  months: string[];
  products: SoilBoardProduct[];
  cells: SoilBoardCell[];
  monthTotals: { month: string; plannedKg: number }[];
  seasonKg: number;
}

export const soilProgramService = {
  async importProgram(csv: string, season?: string, source?: { filename?: string }): Promise<ImportResult> {
    const res = await axios.post<ImportResult>(`${base}/soil-programs/import`, { csv, season, source }, { headers: authHeaders() });
    return res.data;
  },
  async list(): Promise<SoilProgram[]> {
    return (await axios.get<SoilProgram[]>(`${base}/soil-programs`, { headers: authHeaders() })).data;
  },
  async get(id: string): Promise<SoilProgram> {
    return (await axios.get<SoilProgram>(`${base}/soil-programs/${id}`, { headers: authHeaders() })).data;
  },
  async update(id: string, body: { entries?: SoilProgramEntry[]; season?: string }): Promise<SoilProgram> {
    return (await axios.put<SoilProgram>(`${base}/soil-programs/${id}`, body, { headers: authHeaders() })).data;
  },
  async activate(id: string): Promise<SoilProgram> {
    return (await axios.post<SoilProgram>(`${base}/soil-programs/${id}/activate`, {}, { headers: authHeaders() })).data;
  },
  async remove(id: string): Promise<void> {
    await axios.delete(`${base}/soil-programs/${id}`, { headers: authHeaders() });
  },
  /** null when the tenant has no active programme (backend answers 204). */
  async getActive(): Promise<SoilProgram | null> {
    const res = await axios.get<SoilProgram | ''>(`${base}/soil-programs/active`, { headers: authHeaders() });
    return res.status === 204 || !res.data ? null : (res.data as SoilProgram);
  },
  async getBoard(id: string): Promise<SoilBoard> {
    return (await axios.get<SoilBoard>(`${base}/soil-programs/${id}/board`, { headers: authHeaders() })).data;
  },
  async getOrchardNames(): Promise<string[]> {
    const res = await axios.get<Array<{ orchardName: string }>>('/api/v1/orchards', { headers: authHeaders() });
    return res.data.map((o) => o.orchardName);
  },
  /**
   * Orchard facts for the group-set editor's roster. `kg` is 0 by construction —
   * a set is not tied to a month or a product, so it has no planned quantity.
   * The mapping mirrors the backend's getSessionWithFacts exactly.
   */
  async getOrchardFacts(): Promise<SoilSessionFact[]> {
    const res = await axios.get<Array<{ orchardName: string; numberOfTrees?: number; areaHectares?: number; cultivar?: string; variety?: string }>>(
      '/api/v1/orchards', { headers: authHeaders() });
    return res.data.map((o) => ({
      orchardName: o.orchardName, kg: 0,
      treeCount: o.numberOfTrees ?? 0, areaHa: o.areaHectares ?? 0,
      cultivar: o.cultivar || o.variety || '',
    }));
  },
};
